import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, FormHelperText, Stack, Typography } from '@mui/material';

interface SignaturePadProps {
  label: string;
  /** Called with a JPEG data URL when the signature changes, or '' when cleared. */
  onChange: (dataUrl: string) => void;
  /** Existing signature (data URL) to restore. */
  value?: string;
  error?: string;
  height?: number;
  disabled?: boolean;
}

/** JPEG quality — high enough for a legible signature, small enough to send. */
const JPEG_QUALITY = 0.85;

/**
 * Export the canvas as a JPEG data URL.
 *
 * ⚠️ eGOS REQUIREMENT: the `Format` attribute on `eSignatureType` is an
 * enumeration of exactly {`Base64|MIME`, `jpeg`} — PNG is rejected outright, so
 * we must not export `image/png` here.
 *
 * JPEG has no alpha channel: exporting a transparent canvas directly renders
 * the transparent pixels BLACK, producing a solid black signature box. We
 * therefore composite onto an opaque white background first.
 */
function toJpegDataUrl(source: HTMLCanvasElement): string {
  // The backing store is at devicePixelRatio for crisp drawing; exporting it
  // as-is made a Retina/iPad signature 4-9x larger and pushed saves past the
  // API body limit. Export at CSS size instead.
  const ratio = window.devicePixelRatio || 1;
  const flattened = document.createElement('canvas');
  flattened.width = Math.round(source.width / ratio);
  flattened.height = Math.round(source.height / ratio);

  const ctx = flattened.getContext('2d');
  if (!ctx) return source.toDataURL('image/jpeg', JPEG_QUALITY);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, flattened.width, flattened.height);
  ctx.drawImage(source, 0, 0, flattened.width, flattened.height);

  return flattened.toDataURL('image/jpeg', JPEG_QUALITY);
}

/**
 * Lightweight HTML5 canvas signature capture — no external dependency.
 * Supports mouse and touch/pen. Emits a base64 JPEG data URL suitable for the
 * eGOS declaration block (patient/guardian/optometrist sign-off).
 */
export default function SignaturePad({
  label,
  onChange,
  value,
  error,
  height = 160,
  disabled = false,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(Boolean(value));
  // Read in `end()`: the state value is stale when a quick first stroke ends
  // before React re-renders, which dropped the signature.
  const inked = useRef(Boolean(value));

  // Set up the canvas backing store at device pixel ratio for crisp lines.
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a1a1a';
    // Restore an existing signature image if provided.
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
    }
  }, [value]);

  useEffect(() => {
    setupCanvas();
    const handleResize = () => setupCanvas();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pointerPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    drawing.current = true;
    last.current = pointerPos(e);
    canvasRef.current?.setPointerCapture(e.pointerId);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !last.current) return;
    const pos = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
    if (!inked.current) {
      inked.current = true;
      setHasInk(true);
    }
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    const canvas = canvasRef.current;
    if (canvas && inked.current) onChange(toJpegDataUrl(canvas));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    inked.current = false;
    setHasInk(false);
    onChange('');
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={0.5}
      >
        <Typography variant="subtitle2">{label}</Typography>
        <Button size="small" onClick={clear} disabled={disabled || !hasInk}>
          Clear
        </Button>
      </Stack>
      <Box
        sx={{
          border: (theme) =>
            `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
          borderRadius: 1,
          bgcolor: 'background.paper',
          touchAction: 'none',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height,
            display: 'block',
            cursor: disabled ? 'not-allowed' : 'crosshair',
          }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
}
